import { Skeleton } from "@timeghost/ui/shadcn/skeleton";
import type { JSX } from "react";
import { cn } from "@/lib/utils";

/** Left nav pills for focused settings layout. */
export function SettingsFocusedNavLoader({
	count,
	className,
}: {
	readonly count: number;
	readonly className?: string;
}): JSX.Element {
	return (
		<nav
			aria-hidden
			className={cn(
				"flex gap-2 overflow-x-auto lg:sticky lg:top-2 lg:flex-col lg:self-start lg:overflow-visible",
				className
			)}>
			{Array.from({ length: count }, (_, index) => (
				<Skeleton className="h-9 w-28 shrink-0 rounded-lg" key={`nav-${index}`} />
			))}
		</nav>
	);
}

/** Focused settings card: header + body lines. */
export function SettingsFocusedSectionLoader({
	lines = 3,
	contentClassName,
	headerOnly = false,
	className,
}: {
	readonly lines?: number;
	readonly contentClassName?: string;
	readonly headerOnly?: boolean;
	readonly className?: string;
}): JSX.Element {
	return (
		<section
			aria-hidden
			className={cn("rounded-xl border border-border bg-muted", className)}
			data-slot="settings-focused-section-loader">
			<header className="flex items-start gap-3 border-border border-b p-5">
				<Skeleton className="size-9 shrink-0 rounded-lg" />
				<div className="min-w-0 flex-1 space-y-2">
					<Skeleton className="h-4 w-36" />
					<Skeleton className="h-3 w-56 max-w-full" />
				</div>
			</header>
			{headerOnly || lines <= 0 ? null : (
				<div className={cn("space-y-3 p-5", contentClassName)}>
					{Array.from({ length: lines }, (_, index) => (
						<Skeleton className="h-9 w-full" key={`line-${index}`} />
					))}
				</div>
			)}
		</section>
	);
}

/** Edge-to-edge toggle / collapsible strip rows inside a `p-0` section. */
export function SettingsEdgeRowsLoader({ count }: { readonly count: number }): JSX.Element {
	return (
		<div aria-hidden className="flex flex-col">
			{Array.from({ length: count }, (_, index) => (
				<div
					className="flex items-start justify-between gap-3 border-border border-b px-5 py-3.5 last:border-b-0"
					key={`edge-${index}`}>
					<div className="min-w-0 flex-1 space-y-2">
						<Skeleton className="h-4 w-40" />
						<Skeleton className="h-3 w-64 max-w-full" />
					</div>
					<Skeleton className="mt-0.5 h-[18px] w-8 shrink-0 rounded-full" />
				</div>
			))}
		</div>
	);
}

/** 3 tracking-mode style cards. */
export function SettingsModeCardsLoader(): JSX.Element {
	return (
		<div aria-hidden className="grid gap-3 sm:grid-cols-3">
			{Array.from({ length: 3 }, (_, index) => (
				<div className="flex flex-col gap-3 rounded-xl border border-border p-4" key={`mode-${index}`}>
					<div className="flex items-center justify-between">
						<Skeleton className="size-10 rounded-lg" />
						<Skeleton className="size-5 rounded-full" />
					</div>
					<div className="space-y-2">
						<Skeleton className="h-4 w-28" />
						<Skeleton className="h-3 w-full" />
						<Skeleton className="h-3 w-3/4" />
					</div>
				</div>
			))}
		</div>
	);
}

/** Sticky page header: title row + tab triggers. */
export function SettingsPageHeaderLoader({ tabCount }: { readonly tabCount: number }): JSX.Element {
	return (
		<div aria-hidden className="flex w-full min-w-0 items-end gap-0 px-4">
			{Array.from({ length: tabCount }, (_, index) => (
				<Skeleton className="mx-1 mb-0 h-10 w-24 rounded-none" key={`tab-${index}`} />
			))}
		</div>
	);
}
